/**
 * Courier Delivery API Client
 * Epic 71: Futaros Kezbesites
 */

import { api } from './client';

// ============================================
// TYPES
// ============================================

export type CourierDeliveryStatus =
  | 'REQUESTED'
  | 'PAYMENT_PENDING'
  | 'PAID'
  | 'DISPATCHED'
  | 'DELIVERED'
  | 'CANCELLED';

export interface CourierDelivery {
  id: string;
  worksheetId: string;
  address: string;
  scheduledDate?: string;
  courierProvider?: string;
  trackingNumber?: string;
  status: CourierDeliveryStatus;
  courierCost?: number;
  handoffAt?: string;
  deliveredAt?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface RequestDeliveryInput {
  worksheetId: string;
  address: string;
  scheduledDate?: string;
  courierProvider?: string;
  courierCost?: number;
}

export interface CourierHandoffInput {
  courierProvider: string;
  trackingNumber?: string;
}

export interface PaymentLink {
  id: string;
  deliveryId: string;
  url: string;
  amount: number;
  status: string;
  expiresAt: string;
  createdAt: string;
}

export interface CreatePaymentLinkInput {
  amount: number;
  description?: string;
}

// ============================================
// API CALLS
// ============================================

export async function getCourierDeliveries(worksheetId?: string, status?: string) {
  const params = new URLSearchParams();
  if (worksheetId) params.set('worksheetId', worksheetId);
  if (status) params.set('status', status);
  const query = params.toString() ? `?${params.toString()}` : '';
  return api.get<{ data: CourierDelivery[] }>(`/courier-deliveries${query}`);
}

export async function getCourierDeliveryById(id: string) {
  return api.get<{ data: CourierDelivery }>(`/courier-deliveries/${id}`);
}

export async function requestCourierDelivery(data: RequestDeliveryInput) {
  return api.post<{ data: CourierDelivery }>('/courier-deliveries', data);
}

export async function updateCourierStatus(id: string, status: string) {
  return api.patch<{ data: CourierDelivery }>(`/courier-deliveries/${id}/status`, { status });
}

export async function handoffToCourier(id: string, data: CourierHandoffInput) {
  return api.post<{ data: CourierDelivery }>(`/courier-deliveries/${id}/handoff`, data);
}

export async function markDelivered(id: string) {
  return api.post<{ data: CourierDelivery }>(`/courier-deliveries/${id}/delivered`, {});
}

export async function createPaymentLink(deliveryId: string, data: CreatePaymentLinkInput) {
  return api.post<{ data: PaymentLink }>(`/courier-deliveries/${deliveryId}/payment-link`, data);
}
